import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Task } from '@freelancer/datastore/collections';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HoverColor, IconColor, IconSize } from '@freelancer/ui/icon';
import { InputSize, InputType } from '@freelancer/ui/input';
import { Margin } from '@freelancer/ui/margin';
import { SelectItem } from '@freelancer/ui/select';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { TooltipPosition } from '@freelancer/ui/tooltip';
import { maxLength, required } from '@freelancer/ui/validators';
import { isFormControl } from '@freelancer/utils';
import { TaskStatusApi } from 'api-typings/tasklist/tasklist';
import * as Rx from 'rxjs';

@Component({
  selector: 'app-task-form',
  template: `
    <fl-bit>
      <form [formGroup]="taskFormGroup">
        <fl-bit>
          <fl-label
            [size]="TextSize.XXSMALL"
            [weight]="FontWeight.BOLD"
            [for]="'TaskName'"
            i18n="Task edit name label"
          >
            Name <span class="FormLabelRequired">(required)</span>
          </fl-label>
        </fl-bit>
        <fl-bit
          class="FormHeader WebappCompatTasklist-row"
          [flMarginBottom]="Margin.SMALL"
        >
          <ng-container *ngIf="taskFormGroup.get('title') as control">
            <fl-input
              *ngIf="isFormControl(control)"
              class="FormHeader-name"
              i18n-placeholder="Task edit title input placeholder"
              placeholder="Enter title"
              [control]="control"
              [flMarginRight]="Margin.XXSMALL"
              flTrackingLabel="TaskTitle"
            ></fl-input>
          </ng-container>
          <fl-bit class="FormHeader-icons">
            <fl-link
              *ngIf="!isNewTask"
              flTrackingLabel="TaskDelete"
              [flMarginRight]="Margin.XXXSMALL"
              (click)="deleteTaskHandler()"
            >
              <fl-icon
                label="Delete"
                i18n-label="Delete task tooltip"
                [name]="'ui-bin'"
                [color]="IconColor.DARK"
                [hoverColor]="HoverColor.PRIMARY"
              ></fl-icon>
            </fl-link>
            <fl-link
              (click)="closeFormHandler()"
              flTrackingLabel="TaskCloseEdit"
            >
              <fl-icon
                label="Close"
                i18n-label="Close task tooltip"
                [name]="'ui-close'"
                [color]="IconColor.DARK"
                [size]="IconSize.SMALL"
                [hoverColor]="HoverColor.PRIMARY"
              ></fl-icon>
            </fl-link>
          </fl-bit>
        </fl-bit>
        <fl-bit [flMarginBottom]="Margin.MID">
          <fl-bit
            class="WebappCompatTasklist-row"
            [flMarginBottom]="Margin.MID"
          >
            <fl-label
              [size]="TextSize.XXSMALL"
              [weight]="FontWeight.BOLD"
              [for]="'TaskDueDate'"
              i18n="Task edit due date label"
            >
              Due date
            </fl-label>
            <ng-container *ngIf="taskFormGroup.get('dueDate') as control">
              <fl-input
                *ngIf="isFormControl(control)"
                id="'TaskDueDate'"
                [type]="InputType.DATE"
                [size]="InputSize.MID"
                [control]="control"
                flTrackingLabel="TaskDueDate"
              ></fl-input>
            </ng-container>
          </fl-bit>
          <fl-bit class="WebappCompatTasklist-row">
            <fl-label
              [size]="TextSize.XXSMALL"
              [weight]="FontWeight.BOLD"
              [for]="'TaskDescription'"
              i18n="Task edit description label"
            >
              Details
            </fl-label>
            <ng-container *ngIf="taskFormGroup.get('description') as control">
              <fl-textarea
                *ngIf="isFormControl(control)"
                [id]="'TaskDescription'"
                [ngClass]="{ FreelancerViewField: freelancerView }"
                [rows]="5"
                [control]="control"
                [flMarginBottom]="Margin.SMALL"
                placeholder="Describe what the task is about..."
                i18n-placeholder="Task edit description textarea placeholder"
                flTrackingLabel="TaskDescriptionTextarea"
              ></fl-textarea>
            </ng-container>
          </fl-bit>
          <fl-bit class="WebappCompatTasklist-row">
            <fl-label
              [size]="TextSize.XXSMALL"
              [weight]="FontWeight.BOLD"
              [for]="'TaskStatus'"
              i18n="Task edit status label"
            >
              Status
            </fl-label>
            <ng-container *ngIf="taskFormGroup.get('status') as control">
              <fl-select
                *ngIf="isFormControl(control)"
                [id]="'TaskStatus'"
                [ngClass]="{ FreelancerViewField: freelancerView }"
                [control]="control"
                [options]="options"
                flTrackingLabel="TaskStatusSelect"
              ></fl-select>
            </ng-container>
          </fl-bit>
        </fl-bit>
        <fl-bit class="FormFooter WebappCompatTasklist-row">
          <fl-button
            [disabled]="!taskFormGroup.valid"
            [color]="ButtonColor.SECONDARY"
            [size]="ButtonSize.SMALL"
            [busy]="isBusy"
            (click)="saveChangesHandler()"
            flTrackingLabel="TaskSave"
            i18n="Task edit save button"
          >
            Save
          </fl-button>
        </fl-bit>
      </form>
    </fl-bit>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./task-form.component.scss'],
  providers: [DatePipe],
})
export class TaskFormComponent implements OnInit, OnChanges, OnDestroy {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  TextSize = TextSize;
  FontWeight = FontWeight;
  HoverColor = HoverColor;
  IconColor = IconColor;
  IconSize = IconSize;
  InputSize = InputSize;
  InputType = InputType;
  isFormControl = isFormControl;
  Margin = Margin;
  TooltipPosition = TooltipPosition;

  @Input() currentTask?: Partial<Task>;
  @Input() isBusy = false;
  @Input() isDraftTask: boolean;
  @Input() isCleanState: boolean;
  @Input() isNewTask: boolean;
  @Input() freelancerView: boolean;

  @Output() cleanStateChange = new EventEmitter<boolean>();
  @Output() closeForm = new EventEmitter<void>();
  @Output() createTask = new EventEmitter<Partial<Task>>();
  @Output() deleteTask = new EventEmitter<Partial<Task>>();
  @Output() taskEdited = new EventEmitter<Partial<Task>>();
  @Output() updateTask = new EventEmitter<Partial<Task>>();

  taskFormValueChangesSub?: Rx.Subscription;

  options: readonly SelectItem[] = [
    {
      value: TaskStatusApi.IN_PROGRESS,
      displayText: $localize`To-Do`,
    },
    {
      value: TaskStatusApi.CLOSED_RESOLVED,
      displayText: $localize`Completed`,
    },
  ];

  taskFormGroup = this.fb.group({
    title: [
      '',
      [
        required($localize`Please enter a task name.`),
        maxLength(255, $localize`Please enter at most 255 characters.`),
      ],
    ],
    dueDate: [new Date(), []],
    description: [
      '',
      [maxLength(2000, $localize`Please enter at most 2000 characters.`)],
    ],
    status: [
      this.options[0].value,
      [required($localize`Please select task status.`)],
    ],
  });

  constructor(private fb: FormBuilder, private datePipe: DatePipe) {}

  ngOnInit() {
    this.taskFormValueChangesSub = this.taskFormGroup.valueChanges.subscribe(
      taskForm => {
        // If the user has made changes store them in local storage to recover,
        // unsaved changes.
        if (this.taskFormGroup.dirty) {
          this.taskEdited.emit({
            ...this.currentTask,
            ...taskForm,
            dueDate: new Date(this.taskFormGroup.value.dueDate).getTime(),
          });
        }

        if (this.isNewTask) {
          this.setStateOnNewTask(taskForm);
        } else if (!this.isCleanState) {
          this.cleanStateChange.emit(false);
        } else {
          this.cleanStateChange.emit(!this.taskFormGroup.dirty);
        }
      },
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    // TODO: Change to a multiple form component layout in future, this would
    //  remove the need for these checks.
    if (changes.currentTask) {
      // Only set the task form to clean if the task was passed in as clean.
      if (this.cleanStateChange) {
        this.taskFormGroup.markAsPristine();
      }

      // Only update the form with the task information if the task is not new
      // or is a draft project.
      if (!this.isNewTask || this.isDraftTask) {
        if (this.currentTask) {
          this.taskFormGroup.patchValue({
            title: changes.currentTask.currentValue.title,
            dueDate: this.datePipe.transform(
              changes.currentTask.currentValue.dueDate,
              'yyyy-MM-dd',
            ),
            description: changes.currentTask.currentValue.description
              ? changes.currentTask.currentValue.description
              : '',
            status: changes.currentTask.currentValue.status,
          });
        }
      } else if (this.isNewTask) {
        // Clear out the form if a task is being created.
        this.taskFormGroup.patchValue({
          title: '',
          dueDate: new Date(),
          description: '',
          status: this.options[0].value,
        });
      }
    }
  }

  // Called when the save changes button is clicked,
  // updating or creating a task
  saveChangesHandler() {
    // Change dueDate to timestamp in seconds if isset
    if (this.taskFormGroup.value.dueDate) {
      this.taskFormGroup.value.dueDate =
        new Date(this.taskFormGroup.value.dueDate).getTime() / 1000;
    }

    if (this.isNewTask) {
      this.createTask.emit(this.taskFormGroup.value);
    } else {
      this.updateTask.emit(this.taskFormGroup.value);
    }
  }

  // Called when the delete task button is clicked
  deleteTaskHandler() {
    if (this.currentTask) {
      this.deleteTask.emit(this.currentTask);
    }
  }

  closeFormHandler() {
    this.closeForm.emit();
  }

  // If the the form has any of those fields filled,
  // set the clean state to false so as to display a modal
  // for unsaved changes on task switch
  setStateOnNewTask(taskForm: Partial<Task>) {
    if (taskForm.title || taskForm.description) {
      this.cleanStateChange.emit(false);
    } else {
      this.cleanStateChange.emit(true);
    }
  }

  ngOnDestroy() {
    if (this.taskFormValueChangesSub) {
      this.taskFormValueChangesSub.unsubscribe();
    }
  }
}
